<template>
  <transition name="cs-fade">
    <div
      v-if="visible"
      class="cs-overlay"
      @mousedown.self="close"
    >
      <div class="cs-card" role="dialog" aria-modal="true" aria-labelledby="cs-title">
        <button
          type="button"
          class="cs-close"
          aria-label="Close"
          @click="close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <div class="cs-brand">
          <!-- Paper plane with dotted trail -->
          <svg
            class="cs-plane"
            width="66"
            height="55"
            viewBox="0 0 66 55"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M21.4118 9.10523C20.8143 8.23311 20.8089 7.04069 21.4038 6.16322C22.0095 5.3638 23.1481 4.96816 24.1386 5.22927L61.1759 15.394C62.1664 15.6551 62.9201 16.5057 63.0493 17.4424C63.1892 18.4572 62.6939 19.48 61.811 19.9995L28.9074 39.8094C28.0245 40.3289 26.8213 40.2562 26.0112 39.5725C25.201 38.8888 24.8942 37.8175 25.2333 36.8162L30.2645 21.9636L21.4118 9.10523ZM32.6223 22.9111L27.6478 37.5968L57.7574 19.4449L32.6223 22.9111ZM57.4129 16.947L23.5141 7.62216L32.2779 20.4133L57.4129 16.947Z"
              fill="#8866C5"
            />
            <circle cx="20.6754" cy="23.6735" r="2.01722" fill="#8866C5" />
            <circle cx="13.111"  cy="25.6891" r="2.01722" fill="#8866C5" />
            <circle cx="6.05238" cy="29.7243" r="2.01722" fill="#8866C5" />
            <circle cx="2.01722" cy="36.2789" r="2.01722" fill="#8866C5" />
            <circle cx="2.01722" cy="44.3493" r="2.01722" fill="#8866C5" />
            <circle cx="4.0348"  cy="52.9235" r="2.01722" fill="#8866C5" />
          </svg>

          <h2 id="cs-title" class="cs-title">Contract Sent</h2>
        </div>

        <p class="cs-message">
          The contract has now been sent to the client for signing.
          Once they sign it the booking will be complete.
        </p>

        <div class="cs-actions">
          <button
            type="button"
            class="cs-download-btn"
            :disabled="downloading || !attachmentId"
            @click="onDownload"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M10 3v10m0 0l-4-4m4 4l4-4M4 17h12"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>{{ downloading ? 'Preparing…' : 'Download Signed Contract' }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ContractSentModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * Attachment id to download when the user clicks the button.
     * Pass the signed attachment id when available, otherwise the
     * latest contract attachment id.
     */
    attachmentId: {
      type: [Number, String],
      default: null,
    },
    leadEventId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      downloading: false,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        document.addEventListener('keydown', this.onKey);
      } else {
        document.removeEventListener('keydown', this.onKey);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    onKey(e) {
      if (e.key === 'Escape') this.close();
    },
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    async onDownload() {
      if (! this.attachmentId || !this.leadEventId || this.downloading) return;
      this.downloading = true;
      try {
        const res = await this.$store.dispatch(
          'contracts/getDownloadUrl',
          { leadEventId: this.leadEventId, attachmentId: this.attachmentId },
        );
        const url = res && (res.download_url || res.url || res);
        if (url) window.open(url, '_blank', 'noopener');
        this.$emit('download', this.attachmentId);
      } catch (e) {
        // alert/error already dispatched in store
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.cs-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(20, 20, 30, 0.35);
  padding: 24px;
}

.cs-card {
  position: relative;
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(20, 20, 40, 0.18);
  padding: 56px 48px 48px;
  text-align: center;
  font-family: 'Effra', 'Open Sans', sans-serif;
}

.cs-close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #2b2b2b;
  cursor: pointer;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
  padding: 0;
}
.cs-close:hover {
  background: rgba(0, 0, 0, 0.06);
}

.cs-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 4px 0 28px;
}

.cs-plane {
  flex-shrink: 0;
}

.cs-title {
  margin: 0;
  font-family: 'Quincy CF', 'Cormorant Garamond', Georgia, serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1;
  color: #111111;
  letter-spacing: -0.01em;
}

.cs-message {
  margin: 0 auto 36px;
  max-width: 340px;
  font-size: 15px;
  line-height: 1.55;
  color: #4a4a55;
}

.cs-actions {
  display: flex;
  justify-content: center;
}

.cs-download-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 26px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #d6d4dd;
  color: #1f1f25;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.cs-download-btn:hover:not(:disabled) {
  background: #f7f6fb;
  border-color: #bdb8cc;
}
.cs-download-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(110, 74, 255, 0.25);
}
.cs-download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Fade transition */
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.18s ease;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
}
</style>
